import type { IncidentSummary } from '../data/public-status'
import { absoluteUtc, duration } from '../format'

/*
 * An incident, as it appears in a list — active at the top of the page, or
 * historical near the bottom. One component for both, because they are the same
 * fact at different ages and rendering them differently would invite the reader
 * to think one is a different kind of thing.
 *
 * ── `resolved_at === null` means ongoing, and says so ──────────────────────
 *
 * Not a blank, not a dash. An open incident is the most important row on the
 * page and the absence of an end time is what makes it open; rendering that
 * absence as empty space is the same class of mistake as rendering an unmeasured
 * value as zero.
 */

const IMPACT_LABEL: Record<string, string> = {
  none: 'No impact',
  minor: 'Minor',
  major: 'Major',
  critical: 'Critical',
  maintenance: 'Maintenance',
}

const STATE_LABEL: Record<string, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
}

/** Unknown impacts and states render their raw value rather than disappearing:
 *  a backend ahead of this bundle is a reason to show a word the reader can
 *  quote, not a reason to render nothing. */
function label(map: Record<string, string>, key: string): string {
  return map[key] ?? key
}

const IMPACT_TEXT: Record<string, string> = {
  none: 'text-text-muted',
  minor: 'text-caution',
  major: 'text-caution',
  critical: 'text-critical',
  maintenance: 'text-text-secondary',
}

export function IncidentRow({ incident }: { readonly incident: IncidentSummary }) {
  const ongoing = incident.resolvedAt === null
  const span = duration(incident.startedAt, incident.resolvedAt)
  const scope = [...incident.components, ...incident.regions].filter((name) => name !== '')

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        {/*
          `min-w-0 break-words`: the title is operator-written and this page is
          public. A long unbroken token — a hostname, a URL, an error string
          pasted into the field at 3 a.m. — overflows a 320 px row otherwise, and
          the audit only ever sees the titles the fixtures happen to have.
        */}
        <a
          href={`/incidents/${incident.reference}`}
          className="min-w-0 rounded-xs text-sm font-medium break-words text-text-emphasis underline-offset-2 hover:underline"
        >
          {incident.title}
        </a>
        <span className={`text-xs ${IMPACT_TEXT[incident.impact] ?? 'text-text-muted'}`}>
          {label(IMPACT_LABEL, incident.impact)} · {label(STATE_LABEL, incident.state)}
        </span>
      </div>
      <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs text-text-muted">
        {/* `translate="no"`: a reference is an identifier a reader quotes to
            support, and Chrome offers to translate any page it decides is
            foreign. A garbled `PC-INC-A2J8G9` is a reference nobody can use. */}
        <span className="type-data" translate="no">
          {incident.reference}
        </span>
        <span>·</span>
        <time dateTime={incident.startedAt}>{absoluteUtc(incident.startedAt)}</time>
        {ongoing ? (
          <>
            <span>·</span>
            <span className="text-caution">ongoing{span === null ? '' : ` for ${span}`}</span>
          </>
        ) : span === null ? null : (
          <>
            <span>·</span>
            <span>lasted {span}</span>
          </>
        )}
      </p>
      {scope.length === 0 ? null : (
        <p className="mt-1 text-xs text-text-muted">Affected: {scope.join(' · ')}</p>
      )}
    </li>
  )
}

export function IncidentList({
  title,
  incidents,
  empty,
}: {
  readonly title: string
  readonly incidents: readonly IncidentSummary[]
  readonly empty: string
}) {
  return (
    <section className="mt-8">
      <h2 className="type-label">{title}</h2>
      <div className="mt-2 border border-border-default bg-surface-base">
        {incidents.length === 0 ? (
          <p className="px-4 py-3 text-sm text-text-muted">{empty}</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {incidents.map((incident) => (
              <IncidentRow key={incident.reference} incident={incident} />
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
